// Admin-only wording around the shared landing contract (the admin is English
// only). The long check labels live in the contract (CHECK_COPY); the short
// ones below are for one-line summaries ("Not campaign-ready: missing …").
import {
  CHECK_COPY,
  LandingStatus,
  LandingTab,
  RecommendedCheckKey,
  RequiredCheckKey,
} from 'src/@types/landing/landing.contract';

export const SHORT_CHECK_LABEL: Record<RequiredCheckKey, string> = {
  'core.title': 'title',
  'core.description': 'description',
  'core.image': 'cover image',
  'core.price': 'price',
  'hero.tagline': 'promise line',
  'hero.level': 'level',
  'hero.format': 'format',
  duration: 'duration',
  'fit.audience': 'audience',
  'fit.prerequisites': 'prerequisites',
  'outcomes.items': 'outcomes',
  'outcomes.careers': 'career paths',
  'syllabus.modules': 'modules',
  'syllabus.bodies': 'module details',
  'projects.brief': 'project brief',
  'instructor.complete': 'instructor',
  'certificate.complete': 'certificate',
  'batches.schedule': 'schedule',
  'demo.none': 'demo items',
};

export const checkLabel = (key: RequiredCheckKey | RecommendedCheckKey): string =>
  (CHECK_COPY[key] && CHECK_COPY[key].label) || key;

/** "Not campaign-ready: missing level, schedule, outcomes…" ('' when nothing is missing). */
export function notCampaignReady(missing: RequiredCheckKey[]): string {
  if (!missing || !missing.length) return '';
  const first = missing.slice(0, 3).map((k) => SHORT_CHECK_LABEL[k] || k);
  return `Not campaign-ready: missing ${first.join(', ')}${missing.length > 3 ? '…' : ''}`;
}

export const TAB_LABEL: Record<LandingTab, string> = {
  overview: 'Overview',
  fit: 'Who it is for',
  outcomes: 'Outcomes',
  syllabus: 'Syllabus',
  projects: 'Projects',
  people: 'People',
  alumni: 'Past students & their work',
  certificate: 'Certificate',
  pricing: 'Pricing',
  faq: 'FAQ',
  brochure: 'Brochure',
  leads: 'Leads',
};

// A grey second line under the tab names that hide what they hold (the owner
// asks for "prerequisites" and "what you'll learn", not "Who it is for").
export const TAB_HINT: Partial<Record<LandingTab, string>> = {
  fit: 'Audience, prerequisites',
  outcomes: "What you'll learn, careers",
  alumni: 'Batches, stories, projects',
  brochure: 'PDF download',
};

/** Under the editor header and at the top of the Publish dialog (the button keeps its §D.3 name). */
export const LIVE_NOTE = 'Every save is live on the site. Publish marks the page ready for campaigns.';

export type StatusChipColor = 'default' | 'primary' | 'success' | 'warning';

/**
 * The status chip wording (§D.3): nothing in the admin says "draft". An
 * inactive diploma has no public page (the site answers 404), so it never
 * reads "Live" or "Published".
 */
export function landingStatusChip(
  status: LandingStatus,
  met: number,
  total: number,
  belowGate?: boolean,
  active?: boolean
): { label: string; color: StatusChipColor } {
  if (active === false) return { label: 'Off · diploma inactive', color: 'default' };
  const ready = met >= total;
  if (status === 'published') {
    if (!ready || belowGate) return { label: 'Published · below the gate', color: 'warning' };
    return { label: `Published · ${met}/${total}`, color: 'primary' };
  }
  if (ready) return { label: 'Live · ready to publish', color: 'success' };
  return { label: `Live · ${met}/${total}`, color: 'default' };
}

// "New leads since your last visit" (§D.1), per diploma: kayfa.leadsSeenAt is
// a JSON map {diplomaId: ISO time the user last opened that diploma's Leads
// tab}. A diploma never opened counts from LEADS_WINDOW_DAYS ago, so a fresh
// browser still shows the waiting leads. localStorage can throw (private mode),
// so every access is guarded.
const LEADS_SEEN_KEY = 'kayfa.leadsSeenAt';
export const LEADS_WINDOW_DAYS = 30;

export const leadsWindowStart = (now = Date.now()): string =>
  new Date(now - LEADS_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();

const validIso = (v: unknown): v is string => typeof v === 'string' && !Number.isNaN(Date.parse(v));

/** Every stored "last opened" time, by diploma id (the old single-string value reads as {}). */
export function getLeadsSeenMap(): Record<string, string> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LEADS_SEEN_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    Object.keys(parsed).forEach((id) => {
      if (validIso(parsed[id])) out[id] = parsed[id];
    });
    return out;
  } catch {
    return {};
  }
}

/** When this user last opened the diploma's Leads tab, or LEADS_WINDOW_DAYS ago. */
export function getLeadsSeenAt(diplomaId: string, now = Date.now()): string {
  return getLeadsSeenMap()[diplomaId] || leadsWindowStart(now);
}

/** Opening one diploma's Leads tab clears only that diploma's badge. */
export function markLeadsSeen(diplomaId: string): void {
  try {
    const map = getLeadsSeenMap();
    map[diplomaId] = new Date().toISOString();
    window.localStorage.setItem(LEADS_SEEN_KEY, JSON.stringify(map));
  } catch {
    // storage unavailable: the badge simply keeps counting from the last known time
  }
}

// The form path (or DOM anchor) each check jumps to from the checklist.
// 'core-summary' / 'core-batches' are anchors on the Overview's core card.
export const CHECK_FIELD: Record<RequiredCheckKey | RecommendedCheckKey, string> = {
  'core.title': 'core-summary',
  'core.description': 'core-summary',
  'core.image': 'core-summary',
  'core.price': 'core-summary',
  'hero.tagline': 'tagline',
  'hero.level': 'level',
  'hero.format': 'format',
  duration: 'curriculum.modules',
  'fit.audience': 'audience',
  'fit.prerequisites': 'prerequisites',
  'outcomes.items': 'outcomes',
  'outcomes.careers': 'careers',
  'syllabus.modules': 'curriculum.modules',
  'syllabus.bodies': 'curriculum.modules',
  'projects.brief': 'projects-list',
  'instructor.complete': 'instructorIds',
  'certificate.complete': 'certificate.criteria',
  'batches.schedule': 'core-batches',
  'demo.none': 'core-summary',
  'fit.notFor': 'notFor',
  'fit.tech': 'techRequirements',
  'outcomes.six': 'outcomes',
  'outcomes.careersThree': 'careers',
  'syllabus.sessionLength': 'curriculum.sessionMinutes',
  'brochure.ar': 'brochure.ar',
  'brochure.en': 'brochure.en',
  'pricing.installmentPlan': 'installmentPlans',
  'alumni.stories': 'stories',
  'alumni.projects': 'studentProjects',
  'alumni.trackRecord': 'pastCohorts',
  'faq.three': 'faqs',
  'people.support': 'support',
  'hero.selfStudy': 'weeklySelfStudyHours',
  'seo.description': 'seo.description',
  'hero.language': 'instructionLanguage',
};

/** Checks on core fields (edited in Diploma settings, not in this editor). */
export const isCoreCheck = (key: string) => key.indexOf('core.') === 0;

// 'server': the last save was refused over a field on this tab.
export type TabBadge = { kind: 'server' | 'error' | 'warning' | 'ok' | 'empty' | 'none'; count: number };

/**
 * ● error + count = failing required checks on the tab; ◐ warning = a
 * recommended item below threshold; ✓ ok = every check on the tab passes;
 * ○ empty = only recommended checks there and all fail (the content is empty,
 * so it is hidden on the site).
 */
export function tabBadge(
  tab: LandingTab,
  required: { key: string; tab: LandingTab; ok: boolean }[],
  recommended: { key: string; tab: LandingTab; ok: boolean }[]
): TabBadge {
  const req = required.filter((c) => c.tab === tab);
  const rec = recommended.filter((c) => c.tab === tab);
  const failing = req.filter((c) => !c.ok).length;
  if (failing) return { kind: 'error', count: failing };
  const recFailing = rec.filter((c) => !c.ok).length;
  if (!req.length && rec.length && recFailing === rec.length) return { kind: 'empty', count: 0 };
  if (recFailing) return { kind: 'warning', count: recFailing };
  if (req.length || rec.length) return { kind: 'ok', count: 0 };
  return { kind: 'none', count: 0 };
}
